import { LoadingSpinner, PrimaryBlueButton } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useTeams from "@/hooks/useTeams";
import React, { useEffect, useMemo, useState } from "react";
import { SmsIntegrationModel } from "../types";
import SmsIntegrationEditModal from "./SmsIntegrationEditModal";
import SmsIntegrationSetupModal from "./SmsIntegrationSetupModal";
import SmsConsentPanel from "./SmsConsentPanel";

interface SmsUsage {
  smsThisMonth: number;
  maxSmsPerMonth: number | null;
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
    </svg>
  );
}

function numberTypeBadge(phoneNumber: string) {
  const digits = phoneNumber.replace(/\D/g, "");
  if (digits.length === 5 || digits.length === 6) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 ml-2">
        Short Code
      </span>
    );
  }
  if (phoneNumber.startsWith("+1800") || phoneNumber.startsWith("+1888") ||
      phoneNumber.startsWith("+1877") || phoneNumber.startsWith("+1866") ||
      phoneNumber.startsWith("+1855") || phoneNumber.startsWith("+1844") ||
      phoneNumber.startsWith("+1833")) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 ml-2">
        Toll-Free
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 ml-2">
      Local (10DLC)
    </span>
  );
}

export default function SmsIntegration() {
  const [loading, setLoading] = useState(true);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [integrations, setIntegrations] = useState<SmsIntegrationModel[]>([]);
  const [editedIntegrationId, setEditedIntegrationId] = useState<number>();
  const [editError, setEditError] = useState("");
  const [usage, setUsage] = useState<SmsUsage | null>(null);
  const api = useApi();

  const fetchIntegrations = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("Sms/Integrations");
      const data: SmsIntegrationModel[] = await response.json();
      setIntegrations(Array.isArray(data) ? data.filter((i) => !i.isIvr) : []);
    } catch {
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  const fetchUsage = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("Sms/Integrations/Usage");
      const data = await response.json();
      setUsage({
        smsThisMonth: data.smsThisMonth ?? 0,
        maxSmsPerMonth: data.maxSmsPerMonth ?? null
      });
    } catch {
      setUsage(null);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchIntegrations();
    fetchUsage();
  }, [fetchIntegrations, fetchUsage]);

  const { getTeamNameById } = useTeams();

  const errorMap: Record<string, string> = {
    release_error: "An error occurred while releasing the phone number.",
    update_error: "An error occurred while updating the phone number."
  };

  const editedIntegration = useMemo(() => {
    return integrations.find((x) => x.id === editedIntegrationId);
  }, [editedIntegrationId, integrations]);

  async function release() {
    setLoading(true);
    if (window.confirm("Are you sure you want to release this phone number? This cannot be undone.")) {
      try {
        setIntegrations(integrations.filter((i) => i.id !== editedIntegrationId));
        const resp = await api.messaging.delete(`Sms/Integrations/${editedIntegrationId}`);
        if (resp.ok) {
          setEditedIntegrationId(undefined);
          setEditError("");
        }
      } catch {
        setEditError(errorMap["release_error"]);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }

  async function update(integration: SmsIntegrationModel) {
    setLoading(true);
    try {
      const newIntegrations = [...integrations];
      const index = integrations.findIndex((x) => x.id === editedIntegrationId);
      newIntegrations[index] = integration;
      setIntegrations(newIntegrations);

      const resp = await api.messaging.post("Sms/Integrations/UpdatePhoneNumber", {
        Id: editedIntegrationId,
        TeamId: integration.teamId,
        WorkflowId: integration.workflowId ?? null
      });
      if (resp.ok) {
        setEditedIntegrationId(undefined);
        setEditError("");
      } else {
        setEditError(errorMap["update_error"]);
      }
    } catch {
      setEditError(errorMap["update_error"]);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div className="flex justify-center py-20"><LoadingSpinner /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 text-white">
          <PhoneIcon />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">SMS / Text Messaging</h2>
          <p className="text-sm text-slate-500">
            Provision or port phone numbers to send and receive SMS in the Velaro inbox. Powered by
            Twilio. Supports local 10DLC numbers, toll-free numbers, and short codes.
          </p>
        </div>
      </div>

      {/* 10DLC compliance info */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <i className="fas fa-triangle-exclamation text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div className="text-sm text-amber-800">
          <span className="font-semibold">10DLC Compliance:</span> US carriers require all local
          10DLC numbers to be registered for A2P (Application-to-Person) messaging. Velaro handles
          brand and campaign registration automatically when you provision a number through this page.
          Unregistered messages will be blocked or filtered starting in 2024.
        </div>
      </div>

      {/* Usage quota bar */}
      {usage && (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">SMS conversations this month</span>
            <span className="text-sm text-slate-500">
              {usage.smsThisMonth.toLocaleString()}
              {usage.maxSmsPerMonth ? ` / ${usage.maxSmsPerMonth.toLocaleString()}` : " (unlimited)"}
            </span>
          </div>
          {usage.maxSmsPerMonth ? (
            <>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    usage.smsThisMonth / usage.maxSmsPerMonth >= 0.9
                      ? "bg-red-500"
                      : usage.smsThisMonth / usage.maxSmsPerMonth >= 0.7
                      ? "bg-amber-400"
                      : "bg-emerald-500"
                  }`}
                  style={{
                    width: `${Math.min(100, (usage.smsThisMonth / usage.maxSmsPerMonth) * 100)}%`
                  }}
                />
              </div>
              {usage.smsThisMonth >= usage.maxSmsPerMonth && (
                <p className="text-xs text-red-600 mt-1 font-medium">
                  Monthly limit reached — new conversations are blocked until next month or your plan is upgraded.
                </p>
              )}
            </>
          ) : (
            <div className="w-full bg-emerald-100 rounded-full h-2">
              <div className="h-2 w-1/4 rounded-full bg-emerald-400" />
            </div>
          )}
        </div>
      )}

      {/* Connected numbers */}
      {integrations.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full bg-white border border-slate-200 rounded-lg text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Phone Number</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Team</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Workflow</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600" style={{ width: 1 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {integrations.map((integration) => (
                <tr key={integration.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center flex-wrap gap-1">
                      <span className="font-medium text-slate-800 font-mono">
                        {integration.phoneNumber}
                      </span>
                      {numberTypeBadge(integration.phoneNumber)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {getTeamNameById(Number(integration.teamId)) || "All teams"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {integration.workflowName ? (
                      <span className="inline-flex items-center gap-1">
                        <i className="fas fa-diagram-project text-blue-400" aria-hidden="true" />
                        {integration.workflowName}
                        {integration.workflowEnabled === false && (
                          <span className="text-amber-600 font-medium">(disabled)</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Active
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                      onClick={() => setEditedIntegrationId(integration.id)}
                      aria-label={`Edit ${integration.phoneNumber}`}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {integrations.length === 0 && !loading && (
        <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-200">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3 text-emerald-600">
            <PhoneIcon />
          </div>
          <p className="text-slate-600 font-medium">No SMS numbers connected</p>
          <p className="text-slate-400 text-sm mt-1">
            Add a phone number to start sending and receiving SMS in your inbox.
          </p>
        </div>
      )}

      {/* Add button */}
      <div>
        <PrimaryBlueButton
          onClick={() => setShowSetupModal(true)}
          label="Add Phone Number"
        />
        <p className="text-xs text-slate-400 mt-2">
          Buy a new local or toll-free number, port an existing number, or learn about short codes.
        </p>
      </div>

      {/* Opt-in Settings + Consent Audit Log */}
      <SmsConsentPanel />

      {showSetupModal && (
        <SmsIntegrationSetupModal
          onSuccess={() => {
            setShowSetupModal(false);
            fetchIntegrations();
          }}
          onClose={() => setShowSetupModal(false)}
        />
      )}

      {editedIntegration && (
        <SmsIntegrationEditModal
          onClose={() => {
            setEditedIntegrationId(undefined);
            setEditError("");
          }}
          release={release}
          update={update}
          integration={editedIntegration}
          error={editError}
        />
      )}
    </div>
  );
}
